import React from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Button, Flex } from '@opendoor/bricks/core';
import { Sentry } from '@opendoor/observability';

import { OdProtosListingsData_Listing_State as ListingState } from '../../__generated__/athenaGlobalTypes';
import { useSetListingDetails } from '../../graphql/athena/mutations/SetListingDetails';

type ListingFallbackFormProps = {
  listingId: string;
  listingState: ListingState;
};

/** Contains listing fallback buttons */
export const ListingFallbackForm: React.FC<ListingFallbackFormProps> = ({
  listingId,
  listingState,
}) => {
  const { setListingDetails, error, loading } = useSetListingDetails();

  const handleFallbackToPreviousState = async (newState: ListingState) => {
    setListingDetails({
      params: {
        id: listingId,
        nextState: newState,
        updateMask: {
          paths: ['next_state'],
        },
      },
    })
      .then(() => {
        showToast('info', `Successfully reset listing state`, 'lwc-dashboard-toast}', {
          toastOptions: { autoClose: 3000 },
        });
      })
      .catch((e) => {
        Sentry.withScope((scope) => {
          scope.setExtra('listing_id', listingId);
          scope.setExtra('new_state', newState);
          scope.setExtra('error', JSON.stringify(e));
          Sentry.captureException(e);
        });
      });
  };

  if (error) {
    showToast('error', 'Error setting listing state', 'lwc-dashboard-toast', {
      toastOptions: { autoClose: false },
    });
  }

  return (
    <Flex flexDirection="column">
      {listingState === ListingState.LISTING_ACTIVE && (
        <Button
          my={3}
          aria-label="Submit when falling back to listing agreement signed"
          analyticsName="fallback-to-listing-agreement-signed"
          onClick={() => handleFallbackToPreviousState(ListingState.LISTING_AGREEMENT_SIGNED)}
          loading={loading}
        >
          Reset to Listing Agreement Signed
        </Button>
      )}
      {listingState === ListingState.IN_CONTRACT && (
        <Button
          my={3}
          aria-label="Submit when falling back to listed"
          analyticsName="fallback-to-listed"
          onClick={() => handleFallbackToPreviousState(ListingState.LISTING_ACTIVE)}
          loading={loading}
        >
          Reset to Listed
        </Button>
      )}
    </Flex>
  );
};
